import React, { useEffect } from 'react';

import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from '../NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingStepLayout } from '../NewSupergraphOnboardingStepLayout';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  SupergraphOnboardingStepPrivate,
} from '../shared';
import { RegisteredGraphTabContent } from '../yourGraphQLApiStep/RegisteredGraphTabContent';
import { UnregisteredGraphTabContent } from '../yourGraphQLApiStep/UnregisteredGraphTabContent';

import { AwsArnSection } from './AwsArnSection';

export const SetupPrivateGraphQLEndpointStep = ({
  setPreviousStep,
  previousStep,
  values,
  loading,
  ...contentProps
}: React.ComponentProps<typeof SetupPrivateGraphQLEndpointStepContent> &
  Omit<
    React.ComponentProps<typeof NewSupergraphOnboardingStepLayout>,
    'description' | 'image' | 'children' | 'stepId'
  >) => {
  return (
    <NewSupergraphOnboardingStepLayout
      values={values}
      stepId={SupergraphOnboardingStepPrivate.YourSharedResource}
      previousStep={previousStep}
      setPreviousStep={setPreviousStep}
      className="mx-auto"
      hasPrivateSubgraphs={contentProps.hasPrivateSubgraphs}
    >
      <SetupPrivateGraphQLEndpointStepContent
        loading={loading}
        {...contentProps}
        values={values}
      />
    </NewSupergraphOnboardingStepLayout>
  );
};

const SetupPrivateGraphQLEndpointStepContent = ({
  availableUnregisteredResources,
  isDedicatedPlan,
  creatingSupergraph,
  continueAnalyticsLabelsByStep,
  values,
  errors,
  touched,
  nextButtonState,
  setNextButtonState,
  setFieldValue,
  handleBlur,
  validateField,
  schemaUploaded,
  setSchemaUploaded,
  setIsUsingLocalIntrospection,
  whenStepsBegan,
  setWhenStepsBegan,
  selectedPrivateDomainName,
  setSelectedPrivateDomainName,
}: CommonCloudDedicatedProps & {
  nextButtonState: ButtonState;
  schemaUploaded: boolean;
  loading: boolean;
  setNextButtonState: React.Dispatch<React.SetStateAction<ButtonState>>;
  setSchemaUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUsingLocalIntrospection: React.Dispatch<React.SetStateAction<boolean>>;
} & CommonStepProps &
  React.ComponentProps<typeof UnregisteredGraphTabContent> &
  React.ComponentProps<typeof RegisteredGraphTabContent>) => {
  useEffect(() => {
    setNextButtonState({
      isDisabled:
        values.unregisteredEndpointUrl.length < 1 ||
        values.sdl.length < 1 ||
        values.unregisteredSubgraphName.length < 1,
      disabledReason: '',
    });
  }, [
    values.unregisteredEndpointUrl.length,
    values.sdl.length,
    values.unregisteredSubgraphName.length,
    setNextButtonState,
  ]);

  useEffect(() => {
    setWhenStepsBegan((prev) => ({
      ...(prev ?? {}),
      'Connect your private endpoint url': Date.now(),
    }));
  }, [setWhenStepsBegan]);

  return (
    <div>
      <div className="mb-2 text-2xl font-semibold">
        Select your private GraphQL service
      </div>

      <AwsArnSection
        touched={touched}
        errors={errors}
        availableUnregisteredResources={availableUnregisteredResources}
        values={values}
        setFieldValue={setFieldValue}
        handleBlur={handleBlur}
        isDedicatedPlan={isDedicatedPlan}
        validateField={validateField}
        schemaUploaded={schemaUploaded}
        setSchemaUploaded={setSchemaUploaded}
        setIsUsingLocalIntrospection={setIsUsingLocalIntrospection}
        selectedPrivateDomainName={selectedPrivateDomainName}
        setSelectedPrivateDomainName={setSelectedPrivateDomainName}
      />

      <div className="hidden items-center justify-between px-0 py-16 3xl:flex">
        <NewSupergraphOnboardingControls.CancelButton
          creatingSupergraph={creatingSupergraph}
        />

        <NewSupergraphOnboardingControls.BackButton
          creatingSupergraph={creatingSupergraph}
          className="ml-auto"
        />
        <NewSupergraphOnboardingControls.NextButton
          whenStepsBegan={whenStepsBegan}
          smallScreen={false}
          creatingSupergraph={creatingSupergraph}
          state={nextButtonState}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
        />
      </div>
    </div>
  );
};
